import { dropdownStyle } from '../../../../auth';
import './style.css';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import TaperInstruction from './components/TaperInstruction';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { useAddCatalogMed, useGetAmtItems } from './hooks';
import AsyncSelect from 'react-select/async';
import { useMedicationMedCatalogue } from '../MedicationMedCatalogue/context/MedicationMedCatalogueProvider';
import { tranformTaperFormat } from '../../../../lib/transferObjects/Medication';

export const MedForm = ({
  inlineFlex = false,
  data,
  onCancelEdit,
  onOrderSetModeChange,
}) => {
  const [forceRender, setForceRender] = useState(0);
  const { refresh, catalogOptions } = useMedicationMedCatalogue();
  const { postGetAmtItems } = useGetAmtItems();
  const { indicationOptions, therapeuticAreaOptions, therapeuticClassOptions } =
    catalogOptions;

  const { postAddCatalogMed, isLoading: isAdding } = useAddCatalogMed();
  const medForm = useForm({
    defaultValues: {
      medName: data?.med_name || '',
      therapeuticArea: data?.therapeutic_area || '',
      subheading: data?.subheading || '',
      therapeuticClass: data?.therapeutic_class || '',
      brand: data?.brand || '',
      mpIdFreeText: data?.mp_id ? String(data.mp_id) : '',
      mpId:
        data?.mp_id && data?.mp_pt
          ? {
              label: data.mp_pt,
              value: data.mp_id,
            }
          : null,
      mpuuIdFreeText: data?.mpuu_id ? String(data.mpuu_id) : '',
      mpuuId:
        data?.mpuu_id && data?.mpuu_pt
          ? {
              label: data.mpuu_pt,
              value: data.mpuu_id,
            }
          : null,
      tpuuId: data?.tpuu_id
        ? {
            label: data?.tpuu_pt,
            value: data?.tpuu_id,
          }
        : null,
      tpuuIdFreeText: data?.tpuu_id ? String(data.tpuu_id) : '',
      details: data?.details
        ? {
            ...data.details,
            regimen: {
              ...(data.details?.regimen || {}),
              taper: {
                ...(data.details?.regimen?.taper || {}),
                taperHeaders: Array.isArray(
                  data?.details?.regimen?.taper?.taperHeaders,
                )
                  ? data.details.regimen.taper.taperHeaders.map((h) =>
                      typeof h === 'string'
                        ? { title: h }
                        : { title: h?.title ?? '' },
                    )
                  : [],
                taperSchedule: data?.details?.regimen?.taper?.taperSchedule || [
                  { taperDate: '' },
                ],
              },
              prn: {
                prnNote: data?.details?.regimen?.prn?.prnNote || '',
              },
            },
          }
        : {
            indications: [
              {
                indication: data?.details?.indication || '',
                description: data?.details?.description || '',
              },
            ],
            regimen: {
              isDoseSchedule: false,
              isPrn: data?.details?.regimen?.isPrn || false,
              isTaper: false,
              taper: {
                taperHeaders: Array.isArray(
                  data?.details?.regimen?.taper?.taperHeaders,
                )
                  ? data.details.regimen.taper.taperHeaders.map((h) =>
                      typeof h === 'string'
                        ? { title: h }
                        : { title: h?.title ?? '' },
                    )
                  : [],
                taperSchedule: [],
              },
              prn: {
                prnNote: data?.details?.regimen?.prn?.prnNote || '',
              },
            },
          },
      furtherInformation: data?.further_information || '',
    },
    mode: 'onChange',
  });

  const { register, watch, control, formState, reset, setError, clearErrors } =
    medForm;

  const errors = useMemo(() => formState.errors, [formState]);

  const { fields, append } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'details.indications', // unique name for your Field Array
  });

  const handleAddDetails = () => {
    append({
      indication: '',
      description: '',
    });
  };

  const handleMedFormSubmit = async () => {
    const formValues = medForm.getValues();
    const mpIdFreeText =
      typeof formValues.mpIdFreeText === 'string'
        ? formValues.mpIdFreeText.trim()
        : '';
    const mpuuIdFreeText =
      typeof formValues.mpuuIdFreeText === 'string'
        ? formValues.mpuuIdFreeText.trim()
        : '';
    const tpuuIdFreeText =
      typeof formValues.tpuuIdFreeText === 'string'
        ? formValues.tpuuIdFreeText.trim()
        : '';

    const resolvedMpId = mpIdFreeText ? mpIdFreeText : formValues.mpId?.value;
    const resolvedMpuuId = mpuuIdFreeText
      ? mpuuIdFreeText
      : formValues.mpuuId?.value;
    const resolvedTpuuId = tpuuIdFreeText
      ? tpuuIdFreeText
      : formValues.tpuuId?.value;

    const {
      mpIdFreeText: _mpIdFreeText,
      mpuuIdFreeText: _mpuuIdFreeText,
      tpuuIdFreeText: _tpuuIdFreeText,
      ...restFormValues
    } = formValues;
    const payload = {
      ...restFormValues,
      medId: data?.id ?? null,
      mpId: resolvedMpId || null,
      mpuuId: resolvedMpuuId || null,
      tpuuId: resolvedTpuuId || null,
      details: {
        ...formValues.details,
        regimen: {
          ...formValues.details.regimen,
          doseSchedule: {
            ...(formValues.details.regimen?.doseSchedule || {}),
            bedNote: formValues.details.regimen?.doseSchedule?.bedNote ?? '',
            breakfastNote:
              formValues.details.regimen?.doseSchedule?.breakfastNote ?? '',
            lunchNote:
              formValues.details.regimen?.doseSchedule?.lunchNote ?? '',
            dinnerNote:
              formValues.details.regimen?.doseSchedule?.dinnerNote ?? '',
          },
          isDoseSchedule: formValues.details.regimen.isDoseSchedule === 'on',
          isPrn: formValues.details.regimen.isPrn === 'on',
          isTaper: formValues.details.regimen.isTaper === 'on',
          taper: tranformTaperFormat(formValues.details.regimen?.taper),
        },
      },
    };

    if (onOrderSetModeChange) {
      onOrderSetModeChange(payload);
    } else {
      await postAddCatalogMed(payload);
    }
    medForm.reset();
    setForceRender((count) => count + 1);
    onCancelEdit && onCancelEdit();
  };
  const loadMedicinalProductOptions = async (inputValue) => {
    const defaultOptions = data?.mp_id
      ? [
          {
            label: data?.mp_pt,
            value: data?.mp_id,
          },
        ]
      : [];
    if (inputValue && inputValue.length > 2) {
      const result = await postGetAmtItems({
        searchText: inputValue,
        concept: 'mp',
      });
      return result.map((item) => {
        return {
          label: item.medicinal_product,
          value: item.mp_id,
        };
      });
    }
    return defaultOptions;
  };

  const loadMedicinalProductUnitOfUseOptions = async (inputValue) => {
    const defaultOptions = data?.mpuu_id
      ? [
          {
            label: data?.mpuu_pt,
            value: data?.mpuu_id,
          },
        ]
      : [];
    if (inputValue && inputValue.length > 2) {
      const result = await postGetAmtItems({
        searchText: inputValue,
        concept: 'mpuu',
      });
      const seen = new Set();

      const uniqueItems = result
        .filter((item) => {
          if (seen.has(item.mpuu_id)) return false;
          seen.add(item.mpuu_id);
          return true;
        })
        .map((item) => ({
          label: item.medicinal_product_unit_of_use,
          value: item.mpuu_id,
          meta: {
            ...item,
          },
        }));
      return uniqueItems;
    }
    return defaultOptions;
  };

  const loadTradeProductUnitOfUseOptions = async (inputValue) => {
    const defaultOptions = data?.tpuu_id
      ? [
          {
            label: data?.tpuu_pt,
            value: data?.tpuu_id,
            meta: {
              ...data,
            },
          },
        ]
      : [];
    if (inputValue && inputValue.length > 2) {
      const result = await postGetAmtItems({
        searchText: inputValue,
        concept: 'tpuu',
      });
      return result.map((item) => {
        return {
          label: item.trade_product_unit_of_use,
          value: item.tpuu_id,
          meta: {
            ...item,
          },
        };
      });
    }
    return defaultOptions;
  };

  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (hasMountedRef.current) {
      refresh();
    } else {
      hasMountedRef.current = true;
    }
  }, [forceRender]);

  const watchMpId = watch('mpId');
  const watchMpIdFreeText = watch('mpIdFreeText');
  const watchMpuuId = watch('mpuuId');
  const watchMpuuIdFreeText = watch('mpuuIdFreeText');
  const watchTpuuId = watch('tpuuId');
  const watchTpuuIdFreeText = watch('tpuuIdFreeText');

  useEffect(() => {
    const hasMpId =
      !!watchMpId ||
      (typeof watchMpIdFreeText === 'string' &&
        watchMpIdFreeText.trim().length > 0);
    const hasMpuuId =
      !!watchMpuuId ||
      (typeof watchMpuuIdFreeText === 'string' &&
        watchMpuuIdFreeText.trim().length > 0);
    const hasTpuuId =
      !!watchTpuuId ||
      (typeof watchTpuuIdFreeText === 'string' &&
        watchTpuuIdFreeText.trim().length > 0);
    if (!hasMpId && !hasMpuuId && !hasTpuuId) {
      setError('mpId', { message: 'Please select a medicinal product' });
      setError('mpuuId', {
        message: 'Please select a medicinal product unit of use',
      });
      setError('tpuuId', {
        message: 'Please select a trade product unit of use',
      });
    } else {
      clearErrors(['mpId', 'mpuuId', 'tpuuId']);
    }
  }, [
    watchMpId,
    watchMpIdFreeText,
    watchMpuuId,
    watchMpuuIdFreeText,
    watchTpuuId,
    watchTpuuIdFreeText,
    setError,
    clearErrors,
  ]);

  const therapeuticAreaSelectOptions = useMemo(() => {
    return [
      {
        value: '',
        label: 'All',
      },
      ...therapeuticAreaOptions.map((item) => {
        return {
          value: item,
          label: item,
        };
      }),
    ];
  }, [therapeuticAreaOptions]);

  return (
    <>
      <form className="med-form-container">
        <div style={inlineFlex ? { display: 'flex' } : {}}>
          <div style={{ width: '100%' }}>
            <div className="med-form-container-row">
              <div className="filterItem">
                <label className="txtDropdownLabel">
                  Medication name and strength
                </label>
                <div className="divSelect">
                  <div>
                    <input
                      type="text"
                      label="Brand"
                      name="Brand"
                      className="med-input"
                      placeholder="Start typing..."
                      {...register('medName', {
                        required: {
                          value: true,
                          message: 'Please input medication name',
                        },
                      })}
                    />
                    {errors.medName && (
                      <p className="input-error">{errors.medName.message}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="filterItem">
                <div>
                  <label className="txtDropdownLabel">Therapeutic area</label>
                </div>

                <div className="divSelect">
                  <Select
                    onChange={(selectedOptions) => {
                      const therapeuticArea = selectedOptions.value;
                      medForm.setValue('therapeuticArea', therapeuticArea);
                    }}
                    placeholder={'Select'}
                    style={dropdownStyle}
                    options={therapeuticAreaSelectOptions}
                    isMulti={false}
                    value={
                      therapeuticAreaSelectOptions.find(
                        (opt) => opt.value === watch('therapeuticArea'),
                      ) ?? therapeuticAreaSelectOptions[0]
                    }
                  />
                </div>
              </div>
            </div>

            <div className="med-form-container-row">
              <div className="filterItem">
                <label className="txtDropdownLabel">Subheading</label>
                <div>
                  <input
                    type="text"
                    label="Subheading"
                    name="Subheading"
                    className="med-input"
                    placeholder="Start typing..."
                    {...register('subheading')}
                  />
                </div>
              </div>

              <div className="filterItem">
                <label className="txtDropdownLabel">Therapeutic class</label>

                <div className="divSelect">
                  <Select
                    onChange={(selectedOptions) => {
                      const therapeuticArea = selectedOptions.value;
                      medForm.setValue('therapeuticClass', therapeuticArea);
                    }}
                    placeholder={'Select'}
                    style={dropdownStyle}
                    options={therapeuticClassOptions.map((item) => ({
                      value: item,
                      label: item,
                    }))}
                    isMulti={false}
                    value={
                      therapeuticClassOptions
                        .map((item) => ({ value: item, label: item }))
                        .find(
                          (opt) => opt.value === watch('therapeuticClass'),
                        ) ?? { value: '', label: 'Select' }
                    }
                  />
                </div>
              </div>
            </div>

            <div className="med-form-container-row">
              <div className="filterItem">
                <label className="txtDropdownLabel">Brand</label>
                <div>
                  <input
                    type="text"
                    label="Brand"
                    name="Brand"
                    className="med-input"
                    placeholder="Start typing..."
                    {...register('brand')}
                    //error= { !!patientNameValid && formSubmitted}
                    //helperText= { patientNameValid }
                  />
                </div>
              </div>

              <div className="filterItem">
                <div>
                  <label className="txtDropdownLabel">Medicinal product</label>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <Controller
                      name="mpId"
                      control={medForm.control}
                      render={({ field }) => {
                        const selectedOption = field.value || null;

                        return (
                          <AsyncSelect
                            {...field}
                            placeholder="Start typing..."
                            cacheOptions
                            defaultOptions={true}
                            isClearable
                            loadOptions={loadMedicinalProductOptions}
                            value={selectedOption}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption);
                              if (selectedOption) {
                                medForm.setValue(
                                  'mpIdFreeText',
                                  String(selectedOption.value ?? ''),
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  },
                                );
                              } else {
                                medForm.setValue('mpIdFreeText', '', {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </div>

                  <div style={{ width: '100%', minWidth: 0 }}>
                    <input
                      type="text"
                      className="med-input"
                      placeholder="Please enter ID"
                      {...register('mpIdFreeText', {
                        onChange: (event) => {
                          const nextValue =
                            typeof event?.target?.value === 'string'
                              ? event.target.value
                              : '';
                          const currentSelectedOption =
                            medForm.getValues('mpId');
                          const currentSelectedValue =
                            currentSelectedOption?.value
                              ? String(currentSelectedOption.value)
                              : '';

                          if (
                            currentSelectedValue &&
                            nextValue !== currentSelectedValue
                          ) {
                            medForm.setValue('mpId', null, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        },
                      })}
                    />
                  </div>
                </div>
                {errors.mpId && (
                  <p className="input-error">{errors.mpId.message}</p>
                )}
              </div>
            </div>

            <div className="med-form-container-row">
              <div className="filterItem" style={{ maxWidth: '50%' }}>
                {fields.map((field, index) => {
                  return (
                    <div key={field.id} style={{ marginTop: '15px' }}>
                      <label className="txtDropdownLabel">Indication</label>
                      <div>
                        <div className="divSelect">
                          <Controller
                            control={medForm.control}
                            name={`details.indications.${index}.indication`}
                            rules={{
                              required: {
                                value: true,
                                message: 'Please input indication',
                              },
                            }}
                            render={({ field, fieldState: { error } }) => {
                              const selectedOption = indicationOptions
                                .map((item) => ({ value: item, label: item }))
                                .find((option) => option.value === field.value);

                              return (
                                <div className="divSelect">
                                  <Select
                                    value={selectedOption || null} // ensure UI reflects current value
                                    onChange={(selectedOption) => {
                                      if (!selectedOption) {
                                        field.onChange('');
                                      } else {
                                        field.onChange(selectedOption.value); // update form state
                                      }
                                    }}
                                    placeholder="Select"
                                    style={dropdownStyle}
                                    options={indicationOptions.map((item) => ({
                                      value: item,
                                      label: item,
                                    }))}
                                    isClearable={true}
                                    isMultiSelect={false}
                                    showTick={true}
                                  />
                                  {error && (
                                    <p style={{ color: 'red' }}>
                                      {error.message}
                                    </p>
                                  )}
                                </div>
                              );
                            }}
                          />
                        </div>
                      </div>

                      <label
                        className="txtDropdownLabel"
                        style={{ marginTop: '15px' }}
                      >
                        Indication description
                      </label>
                      <div>
                        <input
                          type="text"
                          label="Brand"
                          name="Brand"
                          className="med-input"
                          placeholder="Start typing..."
                          {...register(
                            `details.indications.${index}.description`,
                          )}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="additional-button-container">
                  <button
                    className="additional-button"
                    type="button"
                    onClick={handleAddDetails}
                  >
                    <i className="fa-solid fa-plus fa-md" />
                    <span>Add additional indication</span>
                  </button>
                </div>
              </div>

              <div className="filterItem">
                <label className="txtDropdownLabel">
                  Medicinal product unit of use
                </label>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <Controller
                      name="mpuuId"
                      control={medForm.control}
                      render={({ field }) => {
                        const selectedOption = field.value || null;
                        return (
                          <AsyncSelect
                            {...field}
                            placeholder="Start typing..."
                            cacheOptions
                            defaultOptions={true}
                            isClearable
                            loadOptions={loadMedicinalProductUnitOfUseOptions}
                            value={selectedOption}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption);
                              if (selectedOption) {
                                medForm.setValue(
                                  'mpuuIdFreeText',
                                  String(selectedOption.value ?? ''),
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  },
                                );

                                const currentMpIdFreeText =
                                  medForm.getValues('mpIdFreeText');
                                const hasManualMpId =
                                  typeof currentMpIdFreeText === 'string' &&
                                  currentMpIdFreeText.trim().length > 0;

                                if (!hasManualMpId) {
                                  const mpuId = selectedOption.meta.mp_id;
                                  const mp =
                                    selectedOption.meta.medicinal_product;
                                  medForm.setValue(
                                    'mpIdFreeText',
                                    String(mpuId ?? ''),
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  );
                                  medForm.setValue('mpId', {
                                    value: mpuId,
                                    label: mp,
                                  });
                                }
                              } else {
                                medForm.setValue('mpuuIdFreeText', '', {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                              }
                            }}
                          />
                        );
                      }}
                    />
                  </div>

                  <div style={{ width: '100%', minWidth: 0 }}>
                    <input
                      type="text"
                      className="med-input"
                      placeholder="Please enter ID"
                      {...register('mpuuIdFreeText', {
                        onChange: (event) => {
                          const nextValue =
                            typeof event?.target?.value === 'string'
                              ? event.target.value
                              : '';
                          const currentSelectedOption =
                            medForm.getValues('mpuuId');
                          const currentSelectedValue =
                            currentSelectedOption?.value
                              ? String(currentSelectedOption.value)
                              : '';

                          if (
                            currentSelectedValue &&
                            nextValue !== currentSelectedValue
                          ) {
                            medForm.setValue('mpuuId', null, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        },
                      })}
                    />
                  </div>
                </div>
                {errors.mpuuId && (
                  <p className="input-error">{errors.mpuuId.message}</p>
                )}
                <div>
                  <label
                    className="txtDropdownLabel"
                    style={{ marginTop: '15px' }}
                  >
                    Trade product unit of use
                  </label>
                </div>

                <div>
                  <Controller
                    name="tpuuId"
                    control={medForm.control}
                    render={({ field }) => {
                      const selectedOption = field.value || null;
                      return (
                        <AsyncSelect
                          {...field}
                          placeholder="Start typing..."
                          cacheOptions
                          defaultOptions={true}
                          isClearable
                          loadOptions={loadTradeProductUnitOfUseOptions}
                          onChange={(option) => {
                            field.onChange(option);

                            if (option) {
                              medForm.setValue(
                                'tpuuIdFreeText',
                                String(option.value ?? ''),
                                {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                },
                              );

                              const mpuuId = option.meta.mpuu_id;
                              const mpuu =
                                option.meta.medicinal_product_unit_of_use;
                              const currentMpuuIdFreeText =
                                medForm.getValues('mpuuIdFreeText');
                              const hasManualMpuuId =
                                typeof currentMpuuIdFreeText === 'string' &&
                                currentMpuuIdFreeText.trim().length > 0;

                              if (!hasManualMpuuId) {
                                medForm.setValue(
                                  'mpuuIdFreeText',
                                  String(mpuuId ?? ''),
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  },
                                );
                                medForm.setValue('mpuuId', {
                                  value: mpuuId,
                                  label: mpuu,
                                });
                              }

                              const mpuId = option.meta.mp_id;
                              const mp = option.meta.medicinal_product;
                              const currentMpIdFreeText =
                                medForm.getValues('mpIdFreeText');
                              const hasManualMpId =
                                typeof currentMpIdFreeText === 'string' &&
                                currentMpIdFreeText.trim().length > 0;

                              if (!hasManualMpId) {
                                medForm.setValue(
                                  'mpIdFreeText',
                                  String(mpuId ?? ''),
                                  {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  },
                                );
                                medForm.setValue('mpId', {
                                  value: mpuId,
                                  label: mp,
                                });
                              }
                            } else {
                              medForm.setValue('tpuuIdFreeText', '', {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }
                          }}
                          value={selectedOption}
                        />
                      );
                    }}
                  />
                  <div style={{ width: '100%', minWidth: 0, marginTop: 8 }}>
                    <input
                      type="text"
                      className="med-input"
                      placeholder="Please enter ID"
                      {...register('tpuuIdFreeText', {
                        onChange: (event) => {
                          const nextValue =
                            typeof event?.target?.value === 'string'
                              ? event.target.value
                              : '';
                          const currentSelectedOption =
                            medForm.getValues('tpuuId');
                          const currentSelectedValue =
                            currentSelectedOption?.value
                              ? String(currentSelectedOption.value)
                              : '';

                          if (
                            currentSelectedValue &&
                            nextValue !== currentSelectedValue
                          ) {
                            medForm.setValue('tpuuId', null, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        },
                      })}
                    />
                  </div>
                  {errors.tpuuId && (
                    <p className="input-error">{errors.tpuuId.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <TaperInstruction
            form={medForm}
            basePath="details."
            furtherInform={
              <div
                className="med-form-container-row"
                style={{ minWidth: '240px' }}
              >
                <div className="filterItem">
                  <label className="txtDropdownLabel">
                    Further information
                  </label>
                  <div>
                    <textarea
                      rows="6"
                      label="Brand"
                      name="Brand"
                      className="med-input"
                      placeholder="Start typing..."
                      {...register('furtherInformation')}
                    />
                  </div>
                </div>
              </div>
            }
          />
        </div>
        <div className="d-flex flex-row gap-2 align-items-center mt-4">
          <button
            className="btn"
            type="button"
            style={{ width: '150px', fontWeight: 'bold' }}
            disabled={isAdding}
            onClick={() => {
              reset();
              onCancelEdit && onCancelEdit();
            }}
          >
            Cancel & close
          </button>
          <button
            className="btn btnPrimary"
            type="button"
            style={{ width: '150px' }}
            onClick={handleMedFormSubmit}
            // disabled={isAdding || !formState.isValid || !formState.isDirty}
          >
            Save
          </button>
        </div>
      </form>
    </>
  );
};
